/**
 * Mock 数据模块
 *
 * 当后端代理域名 (http://123.207.32.32:9002/) 不可用时，
 * 提供前端备用数据，保证页面可浏览、播放器可播放/暂停。
 *
 * 音频使用 SoundHelix 提供的免费可播放 MP3 样本，
 * 保证播放、暂停、进度拖动等交互均可用。
 */

// ============================================================
// 可播放音频映射
// ============================================================
// 使用 SoundHelix 免费 MP3 样本，确保播放器可正常播放/暂停
const SOUNDHELIX_BASE = 'https://www.soundhelix.com/examples/mp3'

// mock 歌曲ID => 可播放音频URL
export const MOCK_AUDIO_MAP: Record<number, string> = {
    9000001: `${SOUNDHELIX_BASE}/SoundHelix-Song-1.mp3`,
    9000002: `${SOUNDHELIX_BASE}/SoundHelix-Song-2.mp3`,
    9000003: `${SOUNDHELIX_BASE}/SoundHelix-Song-3.mp3`,
    9000004: `${SOUNDHELIX_BASE}/SoundHelix-Song-4.mp3`,
    9000005: `${SOUNDHELIX_BASE}/SoundHelix-Song-5.mp3`,
    9000006: `${SOUNDHELIX_BASE}/SoundHelix-Song-6.mp3`,
    9000007: `${SOUNDHELIX_BASE}/SoundHelix-Song-7.mp3`,
    9000008: `${SOUNDHELIX_BASE}/SoundHelix-Song-8.mp3`,
}

// 占位封面图（使用 picsum 提供的稳定图片服务）
const COVER = (seed: string, size = 300) => `https://picsum.photos/seed/${seed}/${size}/${size}`

// ============================================================
// Mock 歌曲列表（用于播放列表、排行榜等）
// ============================================================
interface MockSong {
    name: string
    id: number
    ar: { id: number; name: string }[]
    al: { id: number; name: string; picUrl: string; pic_str: string; pic: number }
    dt: number
}

const MOCK_SONGS: MockSong[] = [
    {
        name: '夜空中最亮的星',
        id: 9000001,
        ar: [{ id: 10001, name: '逃跑计划' }],
        al: { id: 20001, name: '世界', picUrl: COVER('song1'), pic_str: '20001', pic: 20001 },
        dt: 252000,
    },
    {
        name: '平凡之路',
        id: 9000002,
        ar: [{ id: 10002, name: '朴树' }],
        al: { id: 20002, name: '猎户星座', picUrl: COVER('song2'), pic_str: '20002', pic: 20002 },
        dt: 318000,
    },
    {
        name: '成都',
        id: 9000003,
        ar: [{ id: 10003, name: '赵雷' }],
        al: { id: 20003, name: '无法长大', picUrl: COVER('song3'), pic_str: '20003', pic: 20003 },
        dt: 326000,
    },
    {
        name: '理想三旬',
        id: 9000004,
        ar: [{ id: 10004, name: '陈鸿宇' }],
        al: { id: 20004, name: '一如年少模样', picUrl: COVER('song4'), pic_str: '20004', pic: 20004 },
        dt: 264000,
    },
    {
        name: '南山南',
        id: 9000005,
        ar: [{ id: 10005, name: '马頔' }],
        al: { id: 20005, name: '孤岛', picUrl: COVER('song5'), pic_str: '20005', pic: 20005 },
        dt: 346000,
    },
    {
        name: '漂洋过海来看你',
        id: 9000006,
        ar: [{ id: 10006, name: '李宗盛' }],
        al: { id: 20006, name: '李宗盛作品集', picUrl: COVER('song6'), pic_str: '20006', pic: 20006 },
        dt: 276000,
    },
    {
        name: '晴天',
        id: 9000007,
        ar: [{ id: 10007, name: '周杰伦' }],
        al: { id: 20007, name: '叶惠美', picUrl: COVER('song7'), pic_str: '20007', pic: 20007 },
        dt: 269000,
    },
    {
        name: '七里香',
        id: 9000008,
        ar: [{ id: 10008, name: '周杰伦' }],
        al: { id: 20008, name: '七里香', picUrl: COVER('song8'), pic_str: '20008', pic: 20008 },
        dt: 299000,
    },
]

// ============================================================
// Mock 歌词
// ============================================================
const MOCK_LYRIC = `[00:00.000] 作曲 : 逃跑计划
[00:01.000] 作词 : 逃跑计划
[00:12.00]夜空中最亮的星 能否听清
[00:18.00]那仰望的人 心底的孤独和叹息
[00:25.00]夜空中最亮的星 能否记起
[00:31.00]曾与我同行 消失在风里的身影
[00:38.00]我祈祷拥有一颗透明的心灵
[00:44.00]和会流泪的眼睛
[00:51.00]给我再去相信的勇气
[00:57.00]越过谎言去拥抱你
[01:04.00]每当我找不到存在的意义
[01:10.00]每当我迷失在黑夜里
[01:16.00]夜空中最亮的星
[01:22.00]请指引我靠近你
[01:30.00]夜空中最亮的星 是否知道
[01:36.00]曾与我同行的身影 如今在哪里
[01:43.00]夜空中最亮的星 是否在意
[01:49.00]等太阳升起 还是意外先来临
[01:56.00]我宁愿所有痛苦都留在心里
[02:02.00]也不愿忘记你的眼睛
[02:09.00]给我再去相信的勇气
[02:15.00]越过谎言去拥抱你
[02:22.00]每当我找不到存在的意义
[02:28.00]每当我迷失在黑夜里
[02:34.00]夜空中最亮的星
[02:40.00]请照亮我前行`

// ============================================================
// 各 API 的 Mock 响应
// ============================================================

/** /banner */
const mockBanner = () => ({
    banners: [
        { targetId: 1, bigImageUrl: COVER('banner1', 730), imageUrl: COVER('banner1', 730), targetType: 1, typeTitle: '新歌首发', s_ctrp: '', url: '' },
        { targetId: 2, bigImageUrl: COVER('banner2', 730), imageUrl: COVER('banner2', 730), targetType: 1, typeTitle: '数字专辑', s_ctrp: '', url: '' },
        { targetId: 3, bigImageUrl: COVER('banner3', 730), imageUrl: COVER('banner3', 730), targetType: 1, typeTitle: '独家专访', s_ctrp: '', url: '' },
        { targetId: 4, bigImageUrl: COVER('banner4', 730), imageUrl: COVER('banner4', 730), targetType: 1, typeTitle: '热门活动', s_ctrp: '', url: '' },
    ],
})

/** /personalized */
const mockPersonalized = () => ({
    result: Array.from({ length: 8 }, (_, i) => ({
        id: 30000 + i,
        type: 0,
        name: ['华语流行精选', '欧美热门单曲', '日系治愈音乐', '韩流舞曲合集', '民谣故事集', '电子节拍精选', '摇滚经典回忆', '古典优雅之声'][i],
        copywriter: '根据你的口味生成',
        picUrl: COVER(`playlist${i}`, 140),
        canDislike: true,
        trackNumberUpdateTime: Date.now(),
        playCount: 1000000 + i * 23456,
        trackCount: 20 + i,
        highQuality: false,
        alg: 'mock',
    })),
})

/** /album/new */
const mockAlbumNew = () => ({
    albums: Array.from({ length: 8 }, (_, i) => ({
        id: 40000 + i,
        name: ['世界', '猎户星座', '无法长大', '一如年少模样', '孤岛', '叶惠美', '七里香', '范特西'][i],
        picUrl: COVER(`album${i}`),
        artist: { name: ['逃跑计划', '朴树', '赵雷', '陈鸿宇', '马頔', '周杰伦', '周杰伦', '周杰伦'][i] },
        artists: [{ name: ['逃跑计划', '朴树', '赵雷', '陈鸿宇', '马頔', '周杰伦', '周杰伦', '周杰伦'][i], id: 10000 + i }],
        publishTime: Date.now() - i * 86400000 * 30,
        size: 10,
        type: '专辑',
        subType: '流行',
    })),
})

/** /artist/list */
const mockArtistList = () => ({
    artists: Array.from({ length: 5 }, (_, i) => ({
        id: 10000 + i,
        name: ['逃跑计划', '朴树', '赵雷', '陈鸿宇', '马頔'][i],
        img1v1Url: COVER(`artist${i}`),
        picUrl: COVER(`artist${i}`),
        img1v1Id: i,
        picId: i,
        musicSize: 30,
        albumSize: 5,
        briefDesc: '知名音乐人',
        followed: false,
        trans: '',
        alias: [],
    })),
})

/** /top/artists */
const mockTopArtists = () => mockArtistList()

/**
 * /playlist/detail - 根据排行榜 ID 返回不同内容
 * 排行榜 ID: TOP_UP_LIST_ID=19723756, TOP_NEW_LIST_ID=3779629, TOP_ORIGIN_LIST_ID=2884035
 */
const mockPlaylistDetail = (id: number) => {
    const nameMap: Record<number, string> = {
        19723756: '飙升榜',
        3779629: '新歌榜',
        2884035: '原创榜',
    }
    return {
        playlist: {
            id,
            name: nameMap[id] || '推荐歌单',
            coverImgUrl: COVER(`ranking${id}`),
            tracks: MOCK_SONGS.map(s => ({
                name: s.name,
                id: s.id,
                ar: s.ar,
                al: s.al,
                dt: s.dt,
            })),
        },
    }
}

/** /song/detail - 返回第一首 mock 歌曲用于默认播放 */
const mockSongDetail = (ids: number | string) => {
    // 无论请求哪个 ID，都返回 mock 歌曲（保证可播放）
    return {
        songs: [MOCK_SONGS[0]],
        code: 200,
    }
}

/** /lyric */
const mockLyric = () => ({
    lrc: { version: 1, lyric: MOCK_LYRIC },
    code: 200,
})

/** /simi/playlist */
const mockSimiPlaylist = () => ({
    playlists: Array.from({ length: 5 }, (_, i) => ({
        id: 50000 + i,
        name: ['相似歌单推荐一', '相似歌单推荐二', '相似歌单推荐三', '相似歌单推荐四', '相似歌单推荐五'][i],
        coverImgUrl: COVER(`simiPlaylist${i}`),
        playCount: 500000 + i * 10000,
        trackCount: 15 + i,
        creator: { nickname: '音乐爱好者', avatarUrl: COVER('creator', 50) },
    })),
    code: 200,
})

/** /simi/song */
const mockSimiSong = () => ({
    songs: MOCK_SONGS.slice(1).map(s => ({
        id: s.id,
        name: s.name,
        artists: s.ar,
        duration: s.dt,
    })),
    code: 200,
})

/** /toplist */
const mockTopList = () => ({
    list: [
        { id: 19723756, name: '飙升榜', coverImgUrl: COVER('ranking1'), updateFrequency: '每日更新', playCount: 9999999 },
        { id: 3779629, name: '新歌榜', coverImgUrl: COVER('ranking2'), updateFrequency: '每周更新', playCount: 8888888 },
        { id: 2884035, name: '原创榜', coverImgUrl: COVER('ranking3'), updateFrequency: '每周更新', playCount: 7777777 },
    ],
})

/** /playlist/catlist */
const mockCatlist = () => ({
    categories: { '0': '全部', '1': '华语', '2': '流行', '3': '摇滚', '4': '民谣' },
    sub: [
        { name: '华语流行', resourceCount: 100, hot: true, category: 1, type: 0, resourceType: 0, imgId: 0, imgUrl: null, activity: false },
        { name: '欧美流行', resourceCount: 80, hot: true, category: 2, type: 0, resourceType: 0, imgId: 0, imgUrl: null, activity: false },
        { name: '独立摇滚', resourceCount: 60, hot: false, category: 3, type: 0, resourceType: 0, imgId: 0, imgUrl: null, activity: false },
        { name: '校园民谣', resourceCount: 40, hot: false, category: 4, type: 0, resourceType: 0, imgId: 0, imgUrl: null, activity: false },
    ],
    code: 200,
})

/** /top/playlist */
const mockTopPlaylist = () => ({
    playlists: Array.from({ length: 35 }, (_, i) => ({
        id: 60000 + i,
        name: `精选歌单 ${i + 1}`,
        coverImgUrl: COVER(`topPlaylist${i}`),
        playCount: 100000 + i * 5000,
        trackCount: 20,
        creator: { nickname: '音乐人', avatarUrl: COVER('creator', 50) },
    })),
    total: 35,
})

/** /album/newest */
const mockAlbumNewest = () => ({
    albums: mockAlbumNew().albums,
    code: 200,
})

/** /dj/catelist */
const mockDjCatelist = () => ({
    categories: Array.from({ length: 6 }, (_, i) => ({
        id: 70000 + i,
        name: ['情感电台', '音乐推荐', '有声书', '脱口秀', '知识分享', '睡前故事'][i],
        picWebUrl: COVER(`djCate${i}`),
        picIPadUrl: COVER(`djCate${i}`),
        picMacUrl: COVER(`djCate${i}`),
        picUWPUrl: COVER(`djCate${i}`),
    })),
    code: 200,
})

/** /dj/recommend/type */
const mockDjRecommend = () => ({
    djRadios: Array.from({ length: 4 }, (_, i) => ({
        id: 80000 + i,
        name: ['深夜电台', '音乐漫谈', '故事角落', '心灵驿站'][i],
        picUrl: COVER(`djRadio${i}`),
        desc: '优质电台节目',
        playCount: 100000 + i * 5000,
        programCount: 50 + i * 5,
        category: '情感',
        secondCategory: '电台',
    })),
    code: 200,
})

/** /dj/radio/hot */
const mockDjHot = () => ({
    djRadios: Array.from({ length: 12 }, (_, i) => ({
        id: 81000 + i,
        name: `热门电台 ${i + 1}`,
        picUrl: COVER(`djHot${i}`),
        desc: '热门电台节目推荐',
        playCount: 200000 + i * 8000,
        programCount: 60 + i,
        category: '音乐',
        secondCategory: '推荐',
        dj: { nickname: '主播', avatarUrl: COVER('dj', 50) },
    })),
    count: 12,
    hasMore: false,
    code: 200,
})

// ============================================================
// Mock 数据路由：根据请求 URL 返回对应 mock 数据
// ============================================================

/**
 * 根据请求配置获取 mock 数据
 * @param url 请求 URL
 * @param params 请求参数
 * @returns mock 数据，若无匹配返回 null
 */
export function getMockData(url: string, params?: Record<string, any>): any {
    if (!url) return null

    switch (url) {
        case '/banner':
            return mockBanner()
        case '/personalized':
            return mockPersonalized()
        case '/album/new':
            return mockAlbumNew()
        case '/album/newest':
            return mockAlbumNewest()
        case '/artist/list':
            return mockArtistList()
        case '/top/artists':
            return mockTopArtists()
        case '/playlist/detail':
            return mockPlaylistDetail(params?.id)
        case '/song/detail':
            return mockSongDetail(params?.ids)
        case '/lyric':
            return mockLyric()
        case '/simi/playlist':
            return mockSimiPlaylist()
        case '/simi/song':
            return mockSimiSong()
        case '/toplist':
            return mockTopList()
        case '/playlist/catlist':
            return mockCatlist()
        case '/top/playlist':
            return mockTopPlaylist()
        case '/dj/catelist':
            return mockDjCatelist()
        case '/dj/recommend/type':
            return mockDjRecommend()
        case '/dj/radio/hot':
            return mockDjHot()
        default:
            return null
    }
}
